import { createTheme, alpha } from '@mui/material/styles';
import { transitions, easing } from './motion';

// The TopoSDS design system for the web admin.
//
// There was no theme before this: every screen rendered default MUI on a
// white page with no container, which is why the vault looked like a
// spreadsheet someone had dropped on the floor. Setting it here fixes
// spacing, type and colour everywhere at once, and gives new screens a
// default that is already right.
//
// Warm and branded: the TopoSDS blue carries through the chrome, the
// neutrals are warmed a few degrees off pure grey so long documents pages
// do not feel clinical, and status colour is used sparingly — a dot or a
// quiet pill, never a full red bar. The data should be the loud part.
//
// Dark mode was added afterwards and is the reason several things below are
// shaped the way they are. The mobile app has had a full dark theme since it
// was written; the admin had none, and hard-coded '#FFFFFF' in four component
// overrides. Two people on the same product, one of whom works at night,
// should not get different answers about whether it supports their machine.

const brand = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#3392FF',
  500: '#0077FF', // the product blue, shared with mobile
  600: '#005FCC',
  700: '#004799',
  800: '#002F66',
  900: '#001833',
};

// Warm slate. Same family as mobile's neutrals, nudged toward paper.
const neutral = {
  25: '#FCFCFB',
  50: '#F8F8F6',
  100: '#F1F1EE',
  200: '#E5E4E0',
  300: '#CFCEC9',
  400: '#9C9B95',
  500: '#6E6D67',
  600: '#52514C',
  700: '#3B3A36',
  800: '#26251F',
  900: '#16150F',
};

// The dark ramp is the light one turned over, not a different palette. Same
// warmth, same steps, so `grey.50` still means "the quietest surface" and
// `grey.900` still means "the loudest text" — which is what the sx props
// scattered through the pages already assume. Inverting the ramp is what lets
// those call sites keep working without being touched.
const neutralDark = {
  25: '#1B1A14',
  50: '#16150F',
  100: '#1E1D17',
  200: '#2C2B24',
  300: '#403F37',
  400: '#6E6D67',
  500: '#9C9B95',
  600: '#BBBAB3',
  700: '#D6D5CE',
  800: '#EBEAE5',
  900: '#F8F8F6',
};

export const vaultColors = {
  brand,
  neutral,
  current: '#0E9F6E',
  soon: '#D97706',
  expired: '#DC2626',
  missing: '#DC2626',
  archived: neutral[400],
};

// Corner radii. MUI's `shape.borderRadius` covers the default, but the
// component overrides below were picking 8, 10, 12, 14 and 16 individually,
// which is five decisions where there are really three: something small and
// inline, the standard surface, and something large and floating.
export const radius = {
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  full: 999,
} as const;

// The one status vocabulary for the whole admin.
//
// There used to be a second, near-identical set of tokens (`dna`) living in
// pages/workflows/workflowUI.tsx, so a "failed" workflow was #EF4444 and an
// "expired" document was #DC2626 — close enough to look like a rendering
// bug rather than a decision. Operations and the vault now read the same
// red. Anything status-coloured pulls from here.
//
// These are CSS custom properties rather than hex strings so that the dozen
// or so files importing `status` keep working verbatim and become dark-mode
// aware for free. A saturated red that reads as urgent on white is close to
// unreadable on near-black, so the dark scheme lifts all four — see the
// `:root` block in `MuiCssBaseline` for the actual values.
//
// One consequence worth knowing: these cannot be passed through MUI's
// `alpha()`, which parses the string. Use `color-mix` or a pre-mixed token if
// you need a translucent status colour.
export const status = {
  ok: 'var(--topo-status-ok)',
  warn: 'var(--topo-status-warn)',
  danger: 'var(--topo-status-danger)',
  neutral: 'var(--topo-status-neutral)',
  info: 'var(--topo-status-info)',
};

/** The raw light-mode values, for the rare caller that needs a real colour. */
export const statusLight = {
  ok: '#0E9F6E',
  warn: '#D97706',
  danger: '#DC2626',
  neutral: neutral[400],
  info: brand[500],
};

// The chrome. The nav is the only dark surface in the product, and these
// are the only places the dark values are written down — the old sidebar
// carried its own unrelated slate palette (rgb(38,42,48) on #ecf0f1) in
// both a stylesheet and inline sx, and they had already drifted apart.
//
// In dark mode the nav cannot stay exactly where it is, or it disappears into
// the page behind it: the same near-black on both sides of an edge is not an
// edge. The variables lift it one step so it still reads as chrome sitting in
// front of content.
export const nav = {
  bg: 'var(--topo-nav-bg)',
  bgRail: 'var(--topo-nav-bg-rail)',
  panel: 'var(--topo-nav-panel)',
  border: 'rgba(255,255,255,0.08)',
  text: 'rgba(255,255,255,0.72)',
  textMuted: 'rgba(255,255,255,0.45)',
  textActive: '#FFFFFF',
  hover: 'rgba(255,255,255,0.06)',
  active: 'rgba(255,255,255,0.10)',
  accent: brand[400],
};

// Layout constants, so the shell and anything that has to size itself
// against the shell agree. FilesShell used to hard-code
// `calc(100vh - 64px)` for a top bar that did not exist.
// The app chrome — the topbar and the icon rail — at 125% of what it
// started as (56 and 68). The rail was cramped: a 9.5px label with the rows
// tight against each other.
//
// The contextual panel is deliberately NOT scaled with them. It is not app
// chrome; it holds the section's own content (the vault's categories,
// Operations' filters) at the same type size as the page beside it, and
// widening it just takes room from the page for no gain.
export const layout = {
  topbarHeight: 70,
  railWidth: 85,
  panelWidth: 232,
};

const theme = createTheme({
  // `media` rather than a class, because the admin has no theme toggle and
  // does not need one — the machine already knows. Adding a switch would be
  // asking the user to tell us something they have told their OS once.
  cssVariables: { colorSchemeSelector: 'media' },

  colorSchemes: {
    light: {
      palette: {
        primary: { main: brand[500], dark: brand[600], light: brand[100], contrastText: '#FFFFFF' },
        success: { main: '#0E9F6E', light: '#E7F7F1', dark: '#07714D' },
        warning: { main: '#D97706', light: '#FEF3E2', dark: '#9A5502' },
        error: { main: '#DC2626', light: '#FDECEC', dark: '#A81B1B' },
        info: { main: brand[500], light: brand[50], dark: brand[700] },
        background: { default: neutral[50], paper: '#FFFFFF' },
        text: { primary: neutral[900], secondary: neutral[500], disabled: neutral[400] },
        divider: neutral[200],
        grey: neutral as any,
      },
    },
    dark: {
      palette: {
        // The 500 blue is a shade too dense to sit on near-black; 400 keeps
        // the brand recognisable at the contrast the background needs.
        primary: { main: brand[400], dark: brand[500], light: brand[800], contrastText: '#0B0A06' },
        success: { main: '#34D399', light: '#14342A', dark: '#6EE7B7' },
        warning: { main: '#FBBF24', light: '#3A2C12', dark: '#FCD34D' },
        error: { main: '#F87171', light: '#3A1D1D', dark: '#FCA5A5' },
        info: { main: brand[400], light: brand[900], dark: brand[300] },
        // Paper lifts off the page rather than matching it. Depth in dark mode
        // is carried by surface lightness, because shadows have nothing to
        // darken.
        background: { default: neutralDark[50], paper: neutralDark[100] },
        text: {
          primary: neutralDark[900],
          secondary: neutralDark[500],
          disabled: neutralDark[400],
        },
        divider: neutralDark[200],
        grey: neutralDark as any,
      },
    },
  },

  shape: { borderRadius: radius.lg },

  typography: {
    fontFamily: [
      'Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto',
      '"Helvetica Neue"', 'Arial', 'sans-serif',
    ].join(','),
    // Headings are tightened and set in a heavier weight; the default MUI
    // scale is too airy for a records product and reads as a marketing page.
    //
    // Tracking is negative and gets more negative as the type grows, because
    // default letter fitting is drawn for reading sizes and leaves display
    // sizes visibly loose. Leading moves the other way — tight at the top of
    // the scale, loosest on body copy.
    h1: { fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.12 },
    h2: { fontSize: '1.625rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.18 },
    h3: { fontSize: '1.375rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.24 },
    h4: { fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h5: { fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.36 },
    h6: { fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '-0.005em', lineHeight: 1.4 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
    // The colour was a literal neutral[500] and so stayed light-grey on a dark
    // page. Pointed at the palette variable, it follows the scheme.
    subtitle2: {
      fontSize: '0.8125rem',
      fontWeight: 600,
      color: 'var(--mui-palette-text-secondary)',
    },
    body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    caption: { fontSize: '0.78125rem', lineHeight: 1.45, letterSpacing: '0.005em' },
    overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.09em', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --topo-status-ok: ${statusLight.ok};
          --topo-status-warn: ${statusLight.warn};
          --topo-status-danger: ${statusLight.danger};
          --topo-status-neutral: ${statusLight.neutral};
          --topo-status-info: ${statusLight.info};
          --topo-nav-bg: ${neutral[900]};
          --topo-nav-bg-rail: #100F0A;
          --topo-nav-panel: #1E1D17;
        }

        @media (prefers-color-scheme: dark) {
          :root {
            /* Lifted, because these sit on near-black rather than on paper.
               The light values hold their meaning but lose their legibility. */
            --topo-status-ok: #34D399;
            --topo-status-warn: #FBBF24;
            --topo-status-danger: #F87171;
            --topo-status-neutral: ${neutralDark[400]};
            --topo-status-info: ${brand[400]};
            /* The nav steps forward so its edge against the page survives. */
            --topo-nav-bg: #0E0D08;
            --topo-nav-bg-rail: #090804;
            --topo-nav-panel: #17160F;
          }
        }

        /* Reduced motion. Not "no feedback" — colour and opacity changes still
           happen, and instantly, so the interface still answers. What goes is
           travel: transforms, and anything long enough to be watched rather
           than felt. Set as a global rule because opting in per component is
           how it ends up covering four files out of eighty-three, which is
           where this app started. */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }

        /* A defined edge for anyone who has asked the OS for more contrast.
           Borders that read as a gentle separation at default contrast read as
           nothing at all here. */
        @media (prefers-contrast: more) {
          :root {
            --mui-palette-divider: ${neutral[500]};
          }
        }

        body {
          background-color: var(--mui-palette-background-default);
          color: var(--mui-palette-text-primary);
        }

        /* One focus ring for the whole app. MUI's default is per-component
           and several of the hand-rolled clickable Boxes had none at all. */
        :focus-visible {
          outline: 2px solid ${brand[500]};
          outline-offset: 2px;
        }

        /* Scrollbars are part of a files app's texture; the default macOS
           overlay bar disappears against a table and makes long lists feel
           bottomless. */
        *::-webkit-scrollbar { width: 10px; height: 10px; }
        *::-webkit-scrollbar-thumb {
          background-color: var(--mui-palette-grey-300);
          border-radius: ${radius.sm}px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        *::-webkit-scrollbar-thumb:hover { background-color: var(--mui-palette-grey-400); }
      `,
    },

    MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },

    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderColor: 'var(--mui-palette-divider)',
          borderRadius: radius.lg,
          // One soft shadow, not MUI's elevation ladder. Depth should read
          // as "this sits on the page", not "this is floating".
          //
          // In dark mode a shadow has nothing to darken, so it is dropped
          // entirely and the border does the separating instead. A black glow
          // on near-black is just a smudge.
          boxShadow: '0 1px 2px rgba(22,21,15,0.04)',
          '@media (prefers-color-scheme: dark)': { boxShadow: 'none' },
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          paddingInline: 14,
          paddingBlock: 7,
          transition: transitions(['background-color', 'border-color', 'box-shadow']),
        },
        sizeSmall: { paddingInline: 10, paddingBlock: 4 },
        containedPrimary: {
          boxShadow: `0 1px 2px ${alpha(brand[900], 0.16)}`,
          '&:hover': { backgroundColor: brand[600] },
          '@media (prefers-color-scheme: dark)': {
            boxShadow: 'none',
            '&:hover': { backgroundColor: brand[300] },
          },
        },
        outlined: {
          borderColor: 'var(--mui-palette-grey-300)',
          color: 'var(--mui-palette-grey-700)',
          '&:hover': {
            borderColor: 'var(--mui-palette-grey-400)',
            backgroundColor: 'var(--mui-palette-grey-50)',
          },
        },
        text: {
          color: 'var(--mui-palette-grey-600)',
          '&:hover': { backgroundColor: 'var(--mui-palette-grey-100)' },
        },
      },
    },

    MuiIconButton: { styleOverrides: { root: { borderRadius: radius.md } } },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.sm, fontWeight: 600, height: 24 },
        sizeSmall: { height: 22, fontSize: 12 },
        outlined: { borderColor: 'var(--mui-palette-grey-300)' },
      },
    },

    MuiTextField: { defaultProps: { size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: 'var(--mui-palette-background-paper)',
          transition: transitions(['border-color']),
          '& fieldset': { borderColor: 'var(--mui-palette-divider)' },
          '&:hover fieldset': { borderColor: 'var(--mui-palette-grey-300)' },
        },
        input: { fontSize: '0.9375rem' },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: 'var(--mui-palette-divider)', paddingBlock: 10 },
        head: {
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--mui-palette-grey-500)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          backgroundColor: 'var(--mui-palette-grey-25, var(--mui-palette-grey-50))',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:last-child td': { borderBottom: 0 } },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'var(--mui-palette-grey-800)',
          color: 'var(--mui-palette-grey-50)',
          fontSize: 12,
          borderRadius: radius.sm,
          paddingInline: 10,
          paddingBlock: 6,
        },
      },
    },

    MuiDialog: {
      styleOverrides: { paper: { borderRadius: radius.xl } },
      defaultProps: {
        // Enter and exit along the same curve, mirrored. A dialog that arrives
        // on one easing and leaves on another reads as two events.
        transitionDuration: { enter: 240, exit: 180 },
      },
    },
    // MUI drops the content's top padding under a title, and the content
    // scrolls (overflow: auto) — so a first field's floating label, which
    // sits ~9px above its box, was cut in half once it shrank. Enough room
    // for the label; the title's own bottom padding does the rest.
    MuiDialogContent: {
      styleOverrides: { root: { '.MuiDialogTitle-root + &': { paddingTop: 10 } } },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: 'var(--mui-palette-divider)' } } },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: radius.full, backgroundColor: 'var(--mui-palette-grey-200)' },
        bar: { borderRadius: radius.full, transitionTimingFunction: easing.standard },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: radius.lg, alignItems: 'center' } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderColor: 'var(--mui-palette-divider)',
          transition: transitions(['background-color', 'color']),
          '&.Mui-selected': {
            backgroundColor: 'var(--mui-palette-primary-light)',
            color: 'var(--mui-palette-primary-dark)',
          },
        },
      },
    },
  },
});

export default theme;
